<template>

  <div class="speaker-detail-dialog" v-if="isOpen">
    <div class="speaker-detail-dialog-container" ref="container">
      <div class="close-button">
        <div v-on:click="closeDialog">X</div>
      </div>
      <div class="speaker-detail-container">
        <div class="speaker-logo">
          <div class="speaker-image"
               :style="[speaker.hasPicture() ? {'background-image':'url('+ speaker.getPictureUrl()+')'} : {} ]"></div>
        </div>
        <div class="speaker-detail-infos">
          <span class="speaker-name">{{ speaker.displayName }}</span>
          <small class="speaker-company">{{ speaker.company }}</small>
          <small class="speaker-position">{{ speaker.position }}</small>
        </div>
      </div>
      <div class="speaker-infos">
        <small class="infos" v-if="speaker.info" v-html="speaker.info"></small>
      </div>
      <div class="speaker-speeches">
        <div class="speech" v-for="speech of speeches">
          <div class="speech-header">
            <div class="speech-category">{{ speech.category }}</div>
            <div class="speech-date">{{ speech.startDate }}</div>
          </div>
          <div class="speech-content">
            <div class="speech-title">{{ speech.title }}</div>
          </div>


          <div class="speech-speakers-multi" v-if="!speech.showInfoInsteadOfSpeakers()">
            <template v-if="speech.speakers.length > 0" v-for="role of roles">
              <template v-if="speech.hasSpeakerWithRole(role)">
                <div class="speaker-container">
                  <div class="speaker-list-multi">
                          <span class="speaker-header">
                            {{
                              speech.getPersonsByRole(role).length > 1 ? role.displayNamePlural : role.displayNameSingular
                            }}:
                          </span>
                    <div class="speaker" v-for="s of speech.getPersonsByRole(role)"
                         :set="speaker=getSpeakerById(s.id)">
                            <span class="speaker-name"
                                  v-if="speaker.displayName">{{ speaker.displayName }}<span
                              v-if="speaker.position || speaker.company || speaker.country">,</span></span>
                      <span class="speaker-position" v-if="speaker.position">{{ speaker.position }}<span
                        v-if="speaker.company || speaker.country">,</span></span>
                      <span class="speaker-company" v-if="speaker.company">{{ speaker.company }}<span
                        v-if="speaker.country">,</span></span>
                      <span class="speaker-country" v-if="speaker.country">{{ speaker.country }}</span>
                    </div>
                  </div>
                  <div class="speaker-images">
                    <template v-for="s of speech.getPersonsByRole(role)">
                      <div class="image" :title="getSpeakerById(s.id).displayName"
                           :style="[getSpeakerById(s.id).hasPicture() ? {'background-image':'url('+ getSpeakerById(s.id).getPictureUrl()+')'} : {} ]" />
                    </template>
                  </div>
                </div>
              </template>
            </template>
          </div>

          <div class="speech-info"
               v-if="!speech.showInfoInsteadOfSpeakers() && speech.info !== undefined && speech.info.length > 0"
               v-html="speech.info">
          </div>

          <div class="speech-speakers-multi speech-infos-multi" v-if="speech.showInfoInsteadOfSpeakers()">
            <div class="speaker-container">
              <div class="speaker-list-multi" v-html="speech.info"></div>
              <div class="speaker-images">
                <template v-if="speech.speakers.length > 0" v-for="(role,index) of roles">
                  <div class="speaker-images-role-container" v-if="speech.hasSpeakerWithRole(role)">
                    <template v-for="s of speech.getPersonsByRole(role)">
                      <div class="image" :title="getSpeakerById(s.id).displayName"
                        :style="[getSpeakerById(s.id).hasPicture() ? {'background-image':'url('+ getSpeakerById(s.id).getPictureUrl()+')'} : {} ]" />

                    </template>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div class="speech-footer">
            <div class="speech-time-container">
              <i class="fas fa-clock"></i>
              {{ speech.beginTime }} - {{ speech.endTime }}
            </div>
            <div class="speech-place">
              <i class="fas fa-map-marker-alt"></i>
              {{ speech.place }}
            </div>
            <div class="speech-language">
              <i class="fas fa-comment-alt-lines"></i>
              {{ getLangForSpeechById(speech.languageID) }}
            </div>
            <div class="speech-translation" v-if="speech.hasTranslation">
              <i class="fas fa-headphones-alt"></i>
              {{ $t("translate") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { Speaker } from "@/entities/Speaker";
import { Speech } from "@/entities/Speech";
import { isDefined } from "@/utils";
import { Role } from "@/entities/Role";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";


@Options({
  props: {
    getLangForSpeech: {
      required: true,
      type: Function
    }
  }
})
@Options({ components: { LazyLoadContainer, LoadingSpinner } })
export default class SpeakerDetailDialog extends Vue {


  private roles: Role[];
  private speeches: Speech[];
  private speaker: Speaker;
  private speakers: Speaker[];
  private isOpen: boolean = false;
  private getLangForSpeech: (id) => string;

  constructor(props) {
    super(props);
  }

  public openDialog(speaker: Speaker, speeches: Speech[], roles: Role[], speakers: Speaker[]): void {
    this.addClickListener();
    this.speeches = speeches;
    this.speaker = speaker;
    this.roles = roles;
    this.speakers = speakers;
    this.isOpen = true;
  }

  private addClickListener() {
    window.addEventListener("click", this.onClick);
  }

  private onClick(event) {
    if (isDefined(this.$refs.container) && this.isOpen) {
      const container = this.$refs.container as any;
      if (isDefined(container)) {
        if (!container.contains(event.target)) {
          this.closeDialog();
        }
      }
    }
  }

  getSpeakerById(id: string): Speaker {
    if (isDefined(this.speakers) && isDefined(id)) {
      const index = this.speakers.findIndex(s => s.id === id);
      if (index > -1) {
        return this.speakers[index];
      }
    }
    return undefined;
  }


  private removeClickListener() {
    window.removeEventListener("click", this.onClick);
  }

  public closeDialog(): void {
    this.removeClickListener();
    this.isOpen = false;
  }

  public getLangForSpeechById(id) {
    if (isDefined(this.getLangForSpeech)) {
      return this.getLangForSpeech(id);
    }
  }


}
</script>

<style scoped lang="scss">

.speaker-detail-dialog {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: opacity .3s ease;

  &-container {
    position: relative;
    padding: 48px;
    width: 100%;
    max-width: 1300px;
    max-height: 80vh;
    overflow: auto;
    overscroll-behavior: contain;
    margin: 0 auto;
    transition: all .3s ease;
    background: #fff;
    box-shadow: 0 0 100px rgb(0 0 0 / 30%);

    .close-button {
      position: absolute;
      top: 0;
      right: 50px;

      > div {
        width: 50px;
        height: 50px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--primary-color);
        border: 2px solid var(--primary-color);
        background-color: white;
        font-size: 1.5rem;
        cursor: pointer;
        position: fixed;
      }
    }

    .speaker-detail-container {
      display: flex;
      flex-direction: row;
      align-items: center;

      .speaker-logo {
        display: flex;
        justify-content: center;
        width: 10vw;
        height: 10vw;

        > div {
          background-color: #d3d3d3;
          border-radius: 100%;
          background-repeat: no-repeat;
          background-position: center center;
          background-size: cover;
          margin: 4px;
          transition: box-shadow 0.2s linear;
          cursor: pointer;

          border: 2px solid var(--primary-color);
          width: 100%;
          max-height: 100%;
          height: 100%;
          max-width: 100%;
          align-self: center;

        }
      }

      .speaker-detail-infos {
        display: flex;
        flex-direction: column;
        padding-left: 64px;
        text-align: left;

        .speaker-name {
          font-size: 1.2rem;
          font-style: italic;
          font-weight: bold;
          margin-bottom: 12px;
        }

        .speaker-company {
          font-weight: bold;
        }

      }


    }

    .speaker-infos {
      text-align: left;
      margin-top: 32px;

      .infos {
        font-size: 1rem;
        line-height: 2;
      }
    }

    .speaker-speeches {
      margin-top: 32px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .speech {
        width: 100%;
        padding: 48px 0;
        border-bottom: 1px solid #bdbdbd;

        .speech-header {
          display: flex;
          flex-direction: row;
          align-items: center;
          border-bottom: 5px solid var(--primary-color);
          padding-bottom: 12px;

          .speech-category {
            margin-right: 12px;
            font-size: 1rem;
            text-transform: uppercase;
            padding: 4px 8px;
            color: white;
            background-color: var(--primary-color);
            border-radius: 25px;
            font-style: italic;
            font-weight: bold;
          }

          .speech-date {
            font-weight: bold;
            font-size: 1.35em;
          }
        }

        .speech-content {
          margin: 16px 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;

          .speech-title {
            text-align: left;
            font-weight: bold;
            font-size: 1.35em;
          }
        }

        .speech-info {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .speech-footer {
          display: flex;
          flex-direction: row;
          width: 100%;

          > div {
            margin-right: 32px;
            font-weight: bold;
            font-size: 1.1rem;
            display: flex;
            align-items: center;
            line-height: 0;

            > i {
              margin-right: 12px;
              font-size: 1.8rem;
              color: var(--primary-color);
            }
          }
        }
      }

      .speech:last-of-type {
        border: none;
        padding-bottom: 0;
        text-align: left;
      }

    }
  }
}
</style>
